import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Share2, MessageCircle, Twitter, Download } from "lucide-react";
import api, { API } from "../lib/api";
import { Spinner, EmptyState } from "../components/states";
import { toast } from "sonner";
import { BackButton, Breadcrumbs, ExternalLink, useEscape } from "../lib/nav";

export default function Highlight() {
  const { id } = useParams();
  const [h, setH] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let t;
    const load = () => api.get(`/highlights/${id}`).then((r) => { setH(r.data); if (r.data.status === "rendering") t = setTimeout(load, 3000); }).catch(() => setErr(true));
    load();
    return () => clearTimeout(t);
  }, [id]);
  if (err) return <EmptyState title="Highlight not found" />;
  if (!h) return <Spinner />;
  const url = window.location.href;
  const text = `${h.title} — ${h.dj_name} · 30s highlight on RAV3.LABS`;
  const video = `${API}/highlights/${id}/video.mp4`;
  const share = async () => { if (navigator.share) { try { await navigator.share({ title: text, url }); return; } catch {} } navigator.clipboard?.writeText(url); toast.success("Link copied"); };
  return (
    <div className="mx-auto max-w-[640px] px-4 sm:px-6 py-8" data-testid="highlight-page">
      <div className="flex items-center gap-4 flex-wrap mb-4"><BackButton fallback={`/recording/${h.recording_id}`} /><Breadcrumbs items={[{ label: "Live", to: "/" }, { label: "Archive", to: "/library" }, { label: h.title, to: `/recording/${h.recording_id}` }, { label: "Highlight" }]} /></div>
      <p className="mono-label text-[#E8552A]">30s HIGHLIGHT</p>
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">{h.title}</h1>
      <p className="mono-label text-[#A1A1AA] mt-1">{h.dj_name} · CLIPPED BY {h.user_name?.toUpperCase() || "A LISTENER"} · {h.views} VIEWS</p>
      <div className="mt-5 rounded-xl overflow-hidden border border-[#27272A] bg-black aspect-square">
        {h.status === "ready" ? <video data-testid="highlight-video" src={video} controls playsInline className="w-full h-full" />
          : h.status === "failed" ? <div className="h-full flex items-center justify-center mono-label text-[#ef4444]">RENDER FAILED</div>
          : <div className="h-full flex flex-col items-center justify-center"><Spinner label="Rendering clip" /></div>}
      </div>
      {h.caption && <p className="text-sm text-[#D4D4D8] mt-3">{h.caption}</p>}
      {h.status === "ready" && (
        <div className="flex flex-wrap gap-2 mt-5">
          <button data-testid="hl-share" onClick={share} className="copper-btn rounded-full px-4 py-2 mono-label flex items-center gap-2"><Share2 size={14} /> SHARE</button>
          <a data-testid="hl-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`} target="_blank" rel="noreferrer" className="border border-[#27272A] rounded-full px-4 py-2 mono-label flex items-center gap-2 hover:border-[#E8552A]"><MessageCircle size={14} /> WHATSAPP</a>
          <a data-testid="hl-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="border border-[#27272A] rounded-full px-4 py-2 mono-label flex items-center gap-2 hover:border-[#E8552A]"><Twitter size={14} /> POST</a>
          <a data-testid="hl-download" href={video} download className="border border-[#27272A] rounded-full px-4 py-2 mono-label flex items-center gap-2 hover:border-[#E8552A]"><Download size={14} /> MP4</a>
        </div>
      )}
      <Link data-testid="hl-full-set" to={`/recording/${h.recording_id}`} className="block mt-8 card-copper rounded-xl p-4 hover:border-[#E8552A]/60"><p className="mono-label text-[#71717A]">LISTEN TO THE FULL SET</p><p className="font-display font-bold mt-1">{h.title} →</p></Link>
    </div>
  );
}
